import { createFileRoute, isRedirect, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import * as z from 'zod';

import { ApiError } from '#features/api/utils/ApiError';
import { Chats } from '#modules/chat/components/Chats';
import { useDismissChatNotification } from '#features/notifications/hooks/useDismissChatNotification';
import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { getChatById } from '#features/chats/model/chats';
import { optionalSearchString } from '#utils/searchParams';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const Route = createFileRoute(
  '/{-$locale}/_layout/_dashboard/_authed/dashboard/chats/$chatId'
)({
  validateSearch: zodValidator(
    z.object({
      prefillMessage: optionalSearchString(),
      // Ставит кнопка «Call back» на пуше о пропущенном звонке (см. firebase-messaging-sw.js):
      // страница чата открывается и сразу набирает собеседника. Приезжает из адреса ЧИСЛОМ –
      // отсюда приведение к строке, иначе разбор роняет весь экран (см. optionalSearchString).
      callback: optionalSearchString(),
    })
  ),
  loader: async ({ params: { chatId }, context }) => {
    if (!UUID_REGEX.test(chatId)) {
      throw redirect({ to: '/{-$locale}/dashboard/chats' });
    }

    try {
      const chat = await context.queryClient.fetchQuery({
        queryKey: ['chatById', chatId],
        queryFn: () => getChatById({ id: chatId }),
      });

      if (!chat?.id) {
        throw redirect({ to: '/{-$locale}/dashboard/chats' });
      }

      return null;
    } catch (error) {
      if (isRedirect(error)) throw error;
      console.error(error);

      if (error instanceof ApiError && error.status === 404) {
        throw redirect({ to: '/{-$locale}/dashboard/chats' });
      }

      // Don't crash the route — component will retry via useSuspenseQuery
      return null;
    }
  },
  component: RouteComponent,
  ssr: false,
});

function RouteComponent() {
  const { chatId } = Route.useParams();
  const { prefillMessage, callback } = Route.useSearch();
  const navigate = Route.useNavigate();

  /**
   * «Перезвонить» срабатывает ровно один раз. Решение живёт в состоянии, а из адреса флаг
   * снимается сразу: оставленный в строке, он звонил бы заново на каждое обновление страницы, а
   * снятый до того, как шапка чата успела смонтироваться, потерял бы звонок вовсе.
   */
  const [autoCall] = useState(() => callback === '1');
  useEffect(() => {
    if (callback) {
      void navigate({ search: (prev) => ({ ...prev, callback: undefined }), replace: true });
    }
  }, [callback, navigate]);

  // Mark this conversation as the one on screen so the global WS handler
  // suppresses the toast + sound for messages that belong to it.
  const setActiveChatId = useActiveChatStore((s) => s.setActiveChatId);
  useEffect(() => {
    setActiveChatId(chatId);
    return () => setActiveChatId(null);
  }, [chatId, setActiveChatId]);

  // Разговор на экране — уведомлению о нём в шторке делать нечего.
  useDismissChatNotification(chatId);

  return <Chats chatId={chatId} prefillMessage={prefillMessage} autoCall={autoCall} />;
}
